'use client';

import type { ReactNode } from 'react';
import type { SurfaceAssetMainContentTab } from '../../../store/layoutStore';
import { useMainContentTabs } from '../../../store/layoutStore';
import { useCanonicalInternalNetworkAsset } from '../../../hooks/api/useSurfaceAssetDetail';
import { useTargetId } from '../../../store/projectStore';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Badge,
  BodyDefault,
  CopyIdButton,
  HeadingItem,
  LabelDefault,
  MetaDefault,
  Skeleton,
} from '@terra/ui-design-system';
import { HierarchyIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { EmptyState } from '../../EmptyState';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import { CanonicalInternalNetworkAssetMergeActions } from './CanonicalInternalNetworkAssetMergeActions';
import { buildInternalNetworkAssetTab } from './internal-network-asset-tabs';

export function CanonicalInternalNetworkAssetDetail({
  tab,
}: {
  tab: SurfaceAssetMainContentTab;
}) {
  const networkId = useTargetId();
  const t = useTranslations('externalNetwork.assetDetail');
  const tList = useTranslations('internalNetwork.assetList');
  const tDetailTabs = useTranslations('externalNetwork.assetDetail.tabs');
  const dateFormatter = useDateFormatter();
  const { navigateInActiveTab } = useMainContentTabs();
  const { asset, relationships, isLoading, isError } =
    useCanonicalInternalNetworkAsset({
      networkId,
      assetId: tab.data.entityId,
    });

  if (isLoading) {
    return (
      <VStack p={6} gap={3} w="full">
        <Skeleton h="32px" w="240px" />
        <Skeleton h="180px" w="full" />
      </VStack>
    );
  }

  if (isError || !asset || !networkId) {
    return (
      <EmptyState
        icon={HierarchyIcon}
        title={tList('loadError')}
        description={tList('loadErrorDescription')}
      />
    );
  }

  return (
    <VStack alignItems="stretch" h="full" minH={0}>
      <HStack px={6} py={4} justifyContent="space-between">
        <VStack alignItems="flex-start" gap={1}>
          <HStack gap={2}>
            <HeadingItem>{asset.displayName}</HeadingItem>
            <Badge
              variant="outline"
              colorPalette={
                asset.presenceStatus === 'ONLINE' ? 'green' : 'gray'
              }
            >
              {tList(`presenceStatus.${asset.presenceStatus}`)}
            </Badge>
            <Badge
              variant="outline"
              colorPalette={
                asset.resolutionState === 'CONFLICTED' ? 'orange' : 'blue'
              }
            >
              {tList(`resolutionState.${asset.resolutionState}`)}
            </Badge>
            {asset.adLinked ? (
              <Badge variant="outline" colorPalette="blue">
                AD
              </Badge>
            ) : null}
          </HStack>
          <HStack gap={2}>
            <MetaDefault color="fg.muted">{asset.id}</MetaDefault>
            <CopyIdButton id={asset.id} entityName={asset.displayName} />
          </HStack>
        </VStack>
        <Badge variant="outline">{tList(`assetKind.${asset.assetKind}`)}</Badge>
      </HStack>
      <Box overflowY="auto" px={6} pb={6}>
        <VStack alignItems="stretch" gap={4}>
          <CanonicalInternalNetworkAssetMergeActions
            networkId={networkId}
            asset={asset}
          />
          <DetailSection title={t('generalDetails')}>
            <DetailRow
              label={t('firstSeen')}
              value={dateFormatter.format(new Date(asset.firstObservedAt))}
            />
            <DetailRow
              label={t('lastSeen')}
              value={dateFormatter.format(new Date(asset.lastObservedAt))}
            />
            <DetailRow
              label={t('ipAddresses')}
              value={asset.addresses.map(({ address }) => address).join(', ')}
            />
            <DetailRow
              label={t('hostnames')}
              value={asset.identifiers
                .filter(({ type }) =>
                  ['HOSTNAME', 'FQDN', 'NETBIOS'].includes(type),
                )
                .map(({ rawValue }) => rawValue)
                .join(', ')}
            />
          </DetailSection>
          <DetailSection title={t('source')}>
            {asset.identifiers.map((identifier) => (
              <DetailRow
                key={identifier.id}
                label={identifier.type}
                value={`${identifier.rawValue} · ${identifier.source} · ${identifier.confidence}%`}
              />
            ))}
            {asset.sourceFreshness.map((freshness) => (
              <DetailRow
                key={freshness.source}
                label={freshness.source}
                value={dateFormatter.format(new Date(freshness.lastObservedAt))}
              />
            ))}
            {asset.resolutionIssues.map((issue) => (
              <DetailRow
                key={issue.id}
                label={tList(`resolutionIssueKind.${issue.kind}`)}
                value={tList(`resolutionIssueStatus.${issue.status}`)}
              />
            ))}
          </DetailSection>
          <DetailSection title={t('service')}>
            {asset.services.map((service) => (
              <DetailRow
                key={service.id}
                label={`${service.port}/${service.protocol}`}
                value={`${service.service ?? service.product ?? service.state} · ${service.state}`}
              />
            ))}
          </DetailSection>
          <DetailSection title={t('cidr')}>
            {asset.segments.map((segment) => (
              <DetailRow
                key={segment.id}
                label={segment.name}
                value={segment.cidrs.join(', ')}
              />
            ))}
          </DetailSection>
          <DetailSection title={tDetailTabs('relationships')}>
            {relationships.map((relationship) => {
              const isAdLink = relationship.type === 'AD_ENTITY';

              return (
                <ClickableDetailRow
                  key={`${relationship.type}:${relationship.id}`}
                  label={relationship.type}
                  value={relationship.label}
                  clickable={isAdLink}
                  onClick={
                    isAdLink
                      ? () => {
                          const entityKind =
                            asset.assetKind === 'DOMAIN_CONTROLLER'
                              ? 'DOMAIN_CONTROLLER'
                              : 'COMPUTER';

                          navigateInActiveTab(
                            buildInternalNetworkAssetTab({
                              entityKind,
                              entityId: relationship.id,
                              label: relationship.label,
                            }),
                          );
                        }
                      : undefined
                  }
                />
              );
            })}
          </DetailSection>
        </VStack>
      </Box>
    </VStack>
  );
}

function DetailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <VStack
      alignItems="stretch"
      gap={2}
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="md"
      p={4}
    >
      <LabelDefault>{title}</LabelDefault>
      {children}
    </VStack>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  if (!value) return null;

  return (
    <HStack justifyContent="space-between" gap={4}>
      <MetaDefault color="fg.muted">{label}</MetaDefault>
      <BodyDefault textAlign="right">{value}</BodyDefault>
    </HStack>
  );
}

function ClickableDetailRow({
  label,
  value,
  clickable,
  onClick,
}: {
  label: string;
  value: string;
  clickable?: boolean;
  onClick?: () => void;
}) {
  if (!value) return null;

  return (
    <HStack
      justifyContent="space-between"
      gap={4}
      cursor={clickable ? 'pointer' : undefined}
      _hover={clickable ? { bg: 'bg.muted' } : undefined}
      borderRadius="sm"
      px={1}
      mx={-1}
      onClick={onClick}
    >
      <MetaDefault color="fg.muted">{label}</MetaDefault>
      <BodyDefault textAlign="right" color={clickable ? 'fg.link' : undefined}>
        {value}
      </BodyDefault>
    </HStack>
  );
}
